/**
 * Currencies that use the dollar sign in common copy and are easy to mistake
 * for USD. We append a code, e.g. (AUD), so ranges stay clear.
 * NZD, SGD, HKD: also "$" in everyday usage for those markets.
 */
const DOLLAR_CURRENCIES_REQUIRING_DISAMBIGUATION = new Set<string>([
  "AUD",
  "CAD",
  "NZD",
  "SGD",
  "HKD",
]);

/**
 * e.g. " (AUD)" when disambiguation is needed; "" for USD/unknown/others.
 */
export function salaryDollarDisambiguationSuffix(
  currency: string | null | undefined
): string {
  const code = (currency ?? "").trim().toUpperCase();
  if (!code) {
    return "";
  }
  if (DOLLAR_CURRENCIES_REQUIRING_DISAMBIGUATION.has(code)) {
    return ` (${code})`;
  }
  return "";
}

/**
 * Renders a salary line like "$100,000 - $150,000" and appends
 * (AUD), (CAD), etc. when the code would otherwise be ambiguous with USD.
 * @param coalesceIdentical — if true, min and max equal render as a single
 *   amount (carousel UI).
 */
export function formatSalaryRangeForDisplay(
  min: number | null | undefined,
  max: number | null | undefined,
  currency?: string | null,
  coalesceIdentical = false
): string {
  if (min == null && max == null) {
    return "";
  }
  if (
    coalesceIdentical &&
    min != null &&
    max != null &&
    min === max
  ) {
    return (
      `$${min.toLocaleString()}` + salaryDollarDisambiguationSuffix(currency)
    );
  }
  if (min == null && max != null) {
    return `Up to $${max.toLocaleString()}` + salaryDollarDisambiguationSuffix(
      currency
    );
  }
  if (min != null && max == null) {
    return `From $${min.toLocaleString()}` + salaryDollarDisambiguationSuffix(
      currency
    );
  }
  if (min != null && max != null) {
    return (
      `$${min.toLocaleString()} - $${max.toLocaleString()}` +
      salaryDollarDisambiguationSuffix(currency)
    );
  }
  return "";
}
